import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowLeft, Bot, CheckCircle2, ChevronDown, Download, History, Home, ImagePlus, LoaderCircle, Maximize2, PackageSearch, Pencil, RefreshCw, RotateCcw, Send, ShieldCheck, ShoppingCart, Sparkles, Timer, UploadCloud, WandSparkles, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FlowStepper } from '../components/FlowStepper';
import { ProductCard } from '../components/ProductCard';
import { BeforeAfter } from '../components/BeforeAfter';
import { SurveyModal } from '../components/SurveyModal';
import { RequirementPanel } from '../components/RequirementPanel';
import { api, ApiClientError } from '../services/api';
import { useDesignerStore } from '../state/session';
import type { Generation, Product, Requirements } from '../types';

const budgetLabels: Record<string, string> = { economical: 'اقتصادی', midrange: 'میان‌رده', luxury: 'لوکس' };
const MAX_OTP_SENDS = 3;
const OTP_RESEND_DELAY_SECONDS = 60;

type OtpFeedback = { tone: 'info' | 'error' | 'success'; message: string } | null;

const normalizePhoneInput = (value: string) => {
  let digits = value
    .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)))
    .replace(/\D/g, '');
  if (digits.startsWith('0098')) digits = `0${digits.slice(4)}`;
  else if (digits.startsWith('98')) digits = `0${digits.slice(2)}`;
  else if (digits.startsWith('9') && digits.length === 10) digits = `0${digits}`;
  return digits.slice(0, 11);
};

const completeProfile = (profile?: Requirements | null) => Boolean(profile?.roomType && profile.style && profile.atmosphere && profile.budgetTier && profile.useCase && profile.preferredCategories?.length);

export function DesignerPage() {
  const store = useDesignerStore();
  const [initializing, setInitializing] = useState(true);
  const [initializationAttempt, setInitializationAttempt] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [name, setName] = useState(''); const [phone, setPhone] = useState(''); const [code, setCode] = useState(''); const [otpSent, setOtpSent] = useState(false);
  const [otpExpiresIn, setOtpExpiresIn] = useState(0);
  const [otpResendIn, setOtpResendIn] = useState(0);
  const [otpSendCount, setOtpSendCount] = useState(0);
  const [otpFeedback, setOtpFeedback] = useState<OtpFeedback>(null);
  const [otpVerified, setOtpVerified] = useState(false);
  const [input, setInput] = useState(''); const [showProducts, setShowProducts] = useState(false);
  const [generation, setGeneration] = useState<Generation | null>(null);
  const [surveyOpen, setSurveyOpen] = useState(false); const [pendingAction, setPendingAction] = useState<'modify' | 'cart' | null>(null);
  const [revisionMode, setRevisionMode] = useState(false);
  const [revisionPreview, setRevisionPreview] = useState(false);
  const [revisionFromResult, setRevisionFromResult] = useState(false);
  const [selectedCartProductIds, setSelectedCartProductIds] = useState<string[]>([]);
  const [pendingCartProductIds, setPendingCartProductIds] = useState<string[]>([]);
  const messagesViewport = useRef<HTMLDivElement>(null);
  const otpInputRef = useRef<HTMLInputElement>(null);
  // افزودن چند محصول به سبد ووکامرس فقط با ناوبری‌های پیاپی در یک تب top-level ممکن است
  // (کوکی نشست فروشگاه در درخواست‌های کراس‌سایت fetch ارسال نمی‌شود). تب را همان لحظه‌ی
  // کلیک رزرو می‌کنیم تا popup blocker آن را مسدود نکند.
  const cartTab = useRef<Window | null>(null);
  const openCartTab = () => {
    if (cartTab.current && !cartTab.current.closed) return cartTab.current;
    const tab = window.open('', '_blank');
    if (tab) {
      tab.document.write('<!doctype html><html lang="fa" dir="rtl"><head><meta charset="utf-8"><title>در حال آماده‌سازی سبد کارو لایت…</title><style>body{font-family:system-ui,sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;color:#1f2937;background:#f8fafc}p{font-size:15px}</style></head><body><p>در حال آماده‌سازی سبد خرید… لطفاً این تب را نبندید.</p></body></html>');
      tab.document.close();
    }
    cartTab.current = tab;
    return tab;
  };
  const closeCartTab = () => {
    try { if (cartTab.current && !cartTab.current.closed) cartTab.current.close(); } catch { /* keep silent */ }
    cartTab.current = null;
  };
  const detachCartTab = () => { cartTab.current = null; };
  // سبد ووکامرس به کوکی نشست مرورگر وصل است و در درخواست‌های کراس‌سایت (fetch) ارسال
  // نمی‌شود؛ بنابراین افزودن هر محصول باید با ناوبری top-level مرورگر به
  // «/cart/?add-to-cart=ID» انجام شود تا فروشگاه خودش نشست و سبد را بسازد. برای یک محصول
  // تب جدید مستقیماً روی صفحه سبد باز می‌شود؛ برای چند محصول، تب رزروشده را مرحله‌به‌مرحله
  // ناوبری می‌کنیم و تب در پایان روی صفحه سبد باقی می‌ماند. ناوبری تب کراس‌اورجین فقط از
  // طریق ست‌کننده‌ی location.href مجاز است — فراخوانی location.assign() بعد از اولین
  // ناوبری به دامنه‌ی فروشگاه با SecurityError شکست می‌خورد و بقیه‌ی محصولات اضافه نمی‌شوند.
  const addProductsToWooCart = async (products: Array<{ addToCartUrl: string }>, cartUrl: string) => {
    if (products.length === 1) {
      closeCartTab();
      if (!window.open(products[0].addToCartUrl, '_blank')) window.location.assign(products[0].addToCartUrl);
      return;
    }
    const tab = openCartTab();
    if (!tab) throw new Error('باز کردن تب فروشگاه توسط مرورگر مسدود شد؛ لطفاً اجازه‌ی باز شدن تب جدید را بدهید و دوباره تلاش کنید.');
    for (const product of products) {
      if (tab.closed) {
        cartTab.current = null;
        throw new Error('تب فروشگاه زودتر از موعد بسته شد و همه‌ی محصولات به سبد اضافه نشدند؛ از صفحه‌ی سبد فروشگاه ادامه دهید.');
      }
      tab.location.href = product.addToCartUrl;
      await new Promise((resolve) => setTimeout(resolve, 5000));
    }
    // حالت پایانی را قطعی می‌کنیم: تب روی صفحه‌ی سبد فروشگاه با همه‌ی محصولات می‌ماند.
    if (!tab.closed) tab.location.href = cartUrl;
    detachCartTab();
  };
  const state = store.state;
  const messages = state?.messages ?? [];
  const recommendations = state?.recommendations ?? [];
  const generations = state?.generations ?? [];
  const limitReached = Boolean(state?.limits?.reached || (state?.limits && state.limits.remaining <= 0));
  const limitMessage = state?.limits ? `کاربر گرامی، شما به محدودیت ${state.limits.limit.toLocaleString('fa-IR')} پیام در روز رسیده‌اید. امکان گفت‌وگو یا تولید تصویر جدید تا شروع روز بعد وجود ندارد.` : '';

  useEffect(() => {
    let active = true;
    setInitializing(true);
    useDesignerStore.getState().setError(null);
    useDesignerStore.getState().ensureSession()
      .catch((error) => { if (active) useDesignerStore.getState().setError(messageOf(error)); })
      .finally(() => { if (active) setInitializing(false); });
    return () => { active = false; };
  }, [initializationAttempt]);
  useEffect(() => {
    const viewport = messagesViewport.current;
    if (!viewport) return;
    viewport.scrollTo({ top: viewport.scrollHeight, behavior: 'smooth' });
  }, [messages.length, revisionMode]);
  useEffect(() => { if (state?.user) { setName(state.user.name); setPhone(state.user.phone); } }, [state?.user]);
  useEffect(() => { if (!revisionPreview) setGeneration(generations[0] ?? null); }, [generations, revisionPreview]);
  useEffect(() => { setSelectedCartProductIds(generation?.products?.map((product) => product.id) ?? []); }, [generation?.id]);
  useEffect(() => {
    if (!otpSent || otpVerified) return;
    otpInputRef.current?.focus();
  }, [otpSent, otpVerified]);
  useEffect(() => {
    if (!otpSent || otpVerified || (otpExpiresIn <= 0 && otpResendIn <= 0)) return;
    const timer = window.setInterval(() => {
      setOtpExpiresIn((value) => Math.max(0, value - 1));
      setOtpResendIn((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [otpSent, otpVerified, otpExpiresIn, otpResendIn]);
  useEffect(() => {
    if (!generation || !['PENDING', 'PROCESSING'].includes(generation.status) || !store.sessionId || !store.sessionToken) return;
    const timer = window.setInterval(async () => {
      try {
        const fresh = await api.generation(store.sessionId!, store.sessionToken!, generation.id);
        setGeneration(fresh);
        if (['COMPLETED', 'FAILED'].includes(fresh.status)) await store.refresh();
      } catch (error) { store.setError(messageOf(error)); }
    }, 2500);
    return () => window.clearInterval(timer);
  }, [generation?.id, generation?.status, store.sessionId, store.sessionToken]);
  useEffect(() => () => { if (filePreview) URL.revokeObjectURL(filePreview); }, [filePreview]);

  const activeStep = useMemo(() => {
    if (!state?.environmentImageUrl) return 0;
    if (state.status !== 'ACTIVE') return 1;
    if (revisionMode || !completeProfile(state.requirements)) return 2;
    if (revisionPreview) return 3;
    if (!generation) return 3;
    return 4;
  }, [state, generation, revisionMode, revisionPreview]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'auto' }); }, [activeStep]);

  const act = async <T,>(operation: () => Promise<T>): Promise<T | undefined> => {
    if (useDesignerStore.getState().busy) return undefined;
    store.setBusy(true); store.setError(null);
    try { return await operation(); }
    catch (error) { store.setError(messageOf(error)); return undefined; }
    finally { store.setBusy(false); }
  };

  const handleFile = (selected?: File) => {
    if (!selected) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(selected.type)) return store.setError('فرمت عکس باید JPG، PNG یا WEBP باشد.');
    if (selected.size > 10 * 1024 * 1024) return store.setError('حجم عکس باید کمتر از ۱۰ مگابایت باشد.');
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFile(selected); setFilePreview(URL.createObjectURL(selected)); store.setError(null);
  };

  const uploadRoom = () => act(async () => {
    if (!file || !store.sessionId || !store.sessionToken) return;
    await api.upload(store.sessionId, store.sessionToken, file); await store.refresh();
  });
  const sendOtp = async () => {
    if (!store.sessionId || !store.sessionToken || otpSendCount >= MAX_OTP_SENDS) return;
    store.setBusy(true); store.setError(null); setOtpFeedback(null);
    try {
      const result = await api.requestOtp(store.sessionId, store.sessionToken, name.trim(), phone);
      const nextSendCount = otpSendCount + 1;
      setOtpSent(true);
      setOtpVerified(false);
      setOtpSendCount(nextSendCount);
      setOtpExpiresIn(result.expiresIn);
      setOtpResendIn(Math.min(OTP_RESEND_DELAY_SECONDS, result.expiresIn));
      setCode('');
      setOtpFeedback(result.mockCode ? { tone: 'info', message: `کد آزمایشی ${result.mockCode} است.` } : null);
    } catch (error) {
      const apiError = error instanceof ApiClientError ? error : null;
      setOtpFeedback({
        tone: 'error',
        message: apiError?.code === 'OTP_RATE_LIMIT'
          ? 'درخواست‌های پیاپی زیاد بوده است؛ یک دقیقه صبر کنید و دوباره بزنید.'
          : messageOf(error),
      });
    } finally {
      store.setBusy(false);
    }
  };
  const verify = async () => {
    if (!store.sessionId || !store.sessionToken || otpExpiresIn <= 0) return;
    store.setBusy(true); store.setError(null); setOtpFeedback(null);
    // تأیید OTP ممکن است چند ثانیه طول بکشد چون سرور منتظر تمام‌شدن تحلیل تصویر
    // اتاق می‌ماند؛ اگر بیش از حد معمول طول کشید، به کاربر اطلاع می‌دهیم تا فکر
    // نکند صفحه گیر کرده یا خطایی رخ داده است.
    const analyzingHintTimer = window.setTimeout(() => {
      setOtpFeedback({ tone: 'info', message: 'هوش مصنوعی در حال تحلیل تصویر اتاق شماست؛ چند لحظه صبر کنید…' });
    }, 2500);
    try {
      const result = await api.verifyOtp(store.sessionId, store.sessionToken, name.trim(), phone, code);
      window.clearTimeout(analyzingHintTimer);
      setOtpVerified(true);
      setOtpFeedback({ tone: 'success', message: 'شماره شما تأیید شد؛ در حال ورود به فضای طراحی…' });
      store.setUserToken(result.token);
      await new Promise((resolve) => window.setTimeout(resolve, 550));
      const refreshed = await store.refresh();
      store.setAccountUser(refreshed.user ?? null);
    } catch (error) {
      window.clearTimeout(analyzingHintTimer);
      const apiError = error instanceof ApiClientError ? error : null;
      if (apiError?.code === 'OTP_EXPIRED' || apiError?.code === 'OTP_NOT_FOUND') {
        setOtpExpiresIn(0);
        setCode('');
        setOtpFeedback(null);
        return;
      }
      setOtpFeedback({
        tone: 'error',
        message: apiError?.code === 'OTP_INVALID'
          ? 'کد واردشده صحیح نیست. چهار رقم پیامک را دوباره بررسی کنید.'
          : apiError?.code === 'OTP_MAX_ATTEMPTS'
            ? 'تعداد تلاش مجاز این کد تمام شد؛ پس از پایان شمارش، کد تازه بگیرید.'
            : messageOf(error),
      });
    } finally {
      store.setBusy(false);
    }
  };
  const editOtpIdentity = () => {
    setOtpSent(false);
    setOtpExpiresIn(0);
    setOtpResendIn(0);
    setCode('');
    setOtpFeedback(null);
  };
  const sendMessage = async (value = input) => {
    const text = value.trim(); if (!text || !store.sessionId || !store.sessionToken) return;
    if (limitReached) return store.setError(limitMessage);
    setInput('');
    const result = await act(async () => {
      const response = await api.message(store.sessionId!, store.sessionToken!, text);
      // پیام و پاسخ فوراً در state محلی نشانده می‌شوند تا کاربر بدون تأخیر یک
      // درخواست شبکه‌ای اضافه، پاسخ را ببیند؛ فقط وقتی محصولات پیشنهادی ممکن است
      // تغییر کرده باشند (پروفایل کامل شده یا محصولی شناسایی شده)، جلسه به‌طور کامل
      // به‌روزرسانی می‌شود.
      store.applyMessageOutcome({ userContent: text, reply: response.reply, requirements: response.requirements, suggestedReplies: response.suggestedReplies });
      if (response.productsIdentified || response.complete) await store.refresh();
      return response;
    });
    if (!result) {
      if (value === input) setInput(text);
      return;
    }
    if (result.fallbackUsed) store.setError('سرویس هوش مصنوعی موقتاً پاسخ نداد؛ برای ادامه تست، پاسخ جایگزین محلی استفاده شد. در محیط Production این حالت غیرفعال است.');
  };
  const confirmAndGenerate = () => act(async () => {
    if (!store.sessionId || !store.sessionToken) return;
    if (limitReached) throw new ApiClientError(limitMessage, 'DAILY_LIMIT_REACHED', 429);
    setRevisionMode(false);
    await api.confirm(store.sessionId, store.sessionToken);
    const result = await api.generate(store.sessionId, store.sessionToken);
    setGeneration(result); await store.refresh(); setRevisionPreview(false); setRevisionFromResult(false);
  });
  const finishRevision = () => {
    const latest = useDesignerStore.getState().state;
    if (!completeProfile(latest?.requirements)) return store.setError('برای بازگشت به پیش‌نمایش، ابتدا اطلاعات لازم را در گفتگو کامل کنید.');
    if (!latest?.recommendations.some((product) => product.selected)) return store.setError('هنوز محصول مناسبی آماده نشده است؛ یک پاسخ دیگر در گفتگو ثبت کنید.');
    store.setError(null);
    setRevisionMode(false);
    setRevisionPreview(revisionFromResult);
  };
  const toggleRecommendation = (product: Product) => act(async () => {
    if (!store.sessionId || !store.sessionToken) return;
    await api.setRecommendation(store.sessionId, store.sessionToken, product.id, !product.selected);
    await store.refresh();
  });
  const removeProduct = (product: Product) => act(async () => {
    if (!store.sessionId || !store.sessionToken) return;
    if (!window.confirm(`«${product.name}» از محصولات این طراحی حذف شود؟`)) return;
    await api.removeRecommendation(store.sessionId, store.sessionToken, product.id); await store.refresh();
  });
  const enterRevisionConversation = async (fromResult: boolean) => {
    if (limitReached) return store.setError(limitMessage);
    const started = await act(async () => {
      if (!store.sessionId || !store.sessionToken) return false;
      await api.startRevision(store.sessionId, store.sessionToken);
      await store.refresh();
      return true;
    });
    if (!started) return;
    setPendingAction(null); setRevisionFromResult(fromResult); setRevisionPreview(false); setRevisionMode(true); setShowProducts(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const beginAction = async (action: 'modify' | 'cart', productIds: string[] = []) => {
    if (!generation) return;
    if (action === 'modify' && limitReached) return store.setError(limitMessage);

    if (!generation.surveySubmitted) {
      setPendingAction(action);
      if (action === 'cart') {
        if (!productIds.length) return store.setError('حداقل یک محصول را برای افزودن به سبد خرید انتخاب کنید.');
        setPendingCartProductIds(productIds);
      }
      setSurveyOpen(true);
      return;
    }

    if (action === 'modify') {
      await enterRevisionConversation(true);
    } else {
      if (!productIds.length) return store.setError('حداقل یک محصول را برای افزودن به سبد خرید انتخاب کنید.');
      setPendingCartProductIds(productIds);
      await continuePurchase(productIds);
    }
  };

  const continuePurchase = async (productIds = pendingCartProductIds) => {
    if (!generation || !store.sessionId || !store.sessionToken) return;
    store.setBusy(true);
    store.setError(null);
    try {
      let result;
      try {
        result = await api.purchase(store.sessionId, store.sessionToken, generation.id, productIds);
      } catch (error) {
        if (error instanceof ApiClientError && error.code === 'SURVEY_REQUIRED') {
          setGeneration({ ...generation, surveySubmitted: false });
          setPendingAction('cart');
          setPendingCartProductIds(productIds);
          setSurveyOpen(true);
          return;
        }
        throw error;
      }
      const purchasable = result.products.filter((product) => product.purchasable);
      const excluded = result.products.filter((product) => !product.purchasable);
      if (!purchasable.length) {
        closeCartTab();
        const allOutOfStock = excluded.length > 0 && excluded.every((product) => product.reason === 'out_of_stock');
        throw new Error(allOutOfStock
          ? 'این محصولات در فروشگاه موقتاً موجود نیستند؛ می‌توانید از صفحه‌ی محصول، زمان موجود شدن را از فروشگاه بپرسید.'
          : 'این محصولات هنوز در فروشگاه قیمت ثبت‌شده ندارند و قابل افزودن به سبد نیستند؛ می‌توانید از صفحه‌ی محصول با فروشگاه تماس بگیرید.');
      }
      if (excluded.length) {
        const outOfStock = excluded.filter((product) => product.reason === 'out_of_stock');
        store.setError(outOfStock.length
          ? `توجه: «${outOfStock.map((product) => product.name).join('، ')}» در فروشگاه موجود نیست و به سبد اضافه نمی‌شود.`
          : `توجه: «${excluded.map((product) => product.name).join('، ')}» در فروشگاه قیمت ثبت‌شده ندارد و به سبد اضافه نمی‌شود.`);
        await new Promise((resolve) => setTimeout(resolve, 2600));
      }
      await addProductsToWooCart(purchasable, result.cartUrl);
      setPendingAction(null);
      setPendingCartProductIds([]);
    } catch (error) {
      store.setError(messageOf(error));
    } finally {
      store.setBusy(false);
    }
  };

  const submitSurvey = async (data: { rating: number; comment: string; tags: string[] }) => {
    if (!generation || !store.sessionId || !store.sessionToken) return;
    // طبق جریان درست، تب فروشگاه فقط بعد از ثبت نظرسنجی باز می‌شود؛ برای چند محصول آن را
    // همین‌جا و در بافتِ خودِ کلیک «ثبت نظرسنجی» رزرو می‌کنیم تا popup blocker مسدودش
    // نکند و اگر ذخیرهٔ نظرسنجی شکست بخورد بلافاصله بسته می‌شود.
    const wantsCart = pendingAction === 'cart' && pendingCartProductIds.length > 1;
    if (wantsCart) openCartTab();
    const saved = await act(() => api.survey(store.sessionId!, store.sessionToken!, { generationId: generation.id, ...data }));
    if (!saved) {
      if (wantsCart) closeCartTab();
      return;
    }

    setSurveyOpen(false);
    setGeneration({ ...generation, surveySubmitted: true });

    if (pendingAction === 'modify') {
      setPendingAction(null);
      await enterRevisionConversation(true);
    } else if (pendingAction === 'cart') {
      await continuePurchase();
      setPendingAction(null);
    } else {
      setPendingAction(null);
      await store.refresh();
    }
  };

  const startNewDesign = async () => {
    const hasProgress = Boolean(state?.environmentImageUrl || messages.length || generation);
    if (hasProgress && !window.confirm('طراحی فعلی بسته شود و یک طراحی تازه از مرحله بارگذاری عکس آغاز شود؟')) return;
    setInitializing(true);
    if (filePreview) URL.revokeObjectURL(filePreview);
    store.reset();
    setFile(null); setFilePreview(null); setName(''); setPhone(''); setCode(''); setOtpSent(false);
    setOtpExpiresIn(0); setOtpResendIn(0); setOtpSendCount(0); setOtpFeedback(null); setOtpVerified(false);
    setInput(''); setShowProducts(false); setGeneration(null); setSurveyOpen(false); setPendingAction(null);
    setRevisionMode(false); setRevisionPreview(false); setRevisionFromResult(false);
    setSelectedCartProductIds([]); setPendingCartProductIds([]);
    try { await useDesignerStore.getState().ensureSession(); }
    catch (error) { useDesignerStore.getState().setError(messageOf(error)); }
    finally { setInitializing(false); }
  };

  if (initializing) return <LoadingScreen label="در حال آماده‌سازی فضای طراحی…" />;
  if (!state) return <InitializationFailure message={store.error} onRetry={() => setInitializationAttempt((attempt) => attempt + 1)} />;

  return <main className="designer-shell">

    <style>{`
  html, body {
    overflow-x: hidden;
    max-width: 100%;
  }

.designer-shell {
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    padding: 0;
    box-sizing: border-box;
    overflow-x: hidden;
  }
  
  @media (min-width: 1024px) {
    .designer-shell {
      max-width: 1400px;
      padding: 0 32px;
    }
  }
  
  @media (min-width: 1280px) {
    .designer-shell {
      max-width: 1480px;
      padding: 0 40px;
    }
  }
  
  @media (min-width: 1536px) {
    .designer-shell {
      max-width: 1700px;
      padding: 0 56px;
    }
  }

  @media (min-width: 768px) and (max-width: 1023px) {
    .designer-shell {
      padding: 0 16px;
    }
  }

  @media (max-width: 767px) {
    .designer-shell {
      padding: 0 12px;
    }
  }

  @media (max-width: 375px) {
    .designer-shell {
      padding: 0 8px;
    }
  }

  .designer-toolbar {
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    width: 100%;
    box-sizing: border-box;
  }

  @media (min-width: 768px) {
    .designer-toolbar {
      padding: 16px 24px;
    }
  }

  .stage,
  .chat-layout,
  .preview-stage,
  .generation-stage,
  .result-stage,
  .failure-card {
    border-radius: 16px;
    margin: 12px 0;
    padding: 16px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  @media (min-width: 768px) {
    .stage,
    .chat-layout,
    .preview-stage,
    .generation-stage,
    .result-stage,
    .failure-card {
      margin: 16px 0;
      padding: 24px;
      border-radius: 20px;
    }
  }

  @media (min-width: 1024px) {
    .stage,
    .chat-layout,
    .preview-stage,
    .generation-stage,
    .result-stage,
    .failure-card {
      margin: 24px 0;
      padding: 32px;
      border-radius: 24px;
    }
  }

  .upload-panel,
  .form-card,
  .chat-card,
  .summary-card,
  .direction-image,
  .recommendation-block,
  .generation-copy,
  .result-details,
  .adjustment-card,
  .identified-products,
  .sticky-image {
    border-radius: 12px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .dropzone {
    border-radius: 16px;
    padding: 24px 16px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  @media (min-width: 768px) {
    .dropzone {
      padding: 48px 32px;
    }
  }

  .primary-button {
    border-radius: 12px;
    padding: 14px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    white-space: nowrap;
  }

  @media (min-width: 640px) {
    .primary-button {
      width: auto;
      padding: 16px 32px;
      font-size: 16px;
    }
  }

  .primary-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  .secondary-button {
    border-radius: 12px;
    padding: 14px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    white-space: nowrap;
  }

  @media (min-width: 640px) {
    .secondary-button {
      width: auto;
      padding: 16px 32px;
      font-size: 16px;
    }
  }

  .field input,
  .field textarea,
  .chat-input textarea,
  .otp-input,
  .adjustment-card textarea {
    border-radius: 10px;
    padding: 14px 16px;
    font-size: 16px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    transition: all 0.2s ease;
  }

  .field input:focus,
  .field textarea:focus,
  .chat-input textarea:focus {
    outline: none;
  }

  .message.assistant {
    border-radius: 16px 16px 4px 16px;
    padding: 14px;
    margin: 8px 0;
    max-width: 90%;
    width: auto;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  @media (min-width: 768px) {
    .message.assistant {
      max-width: 75%;
      padding: 20px;
    }
  }

  .message.user {
    border-radius: 16px 16px 16px 4px;
    padding: 14px;
    margin: 8px 0 8px auto;
    max-width: 90%;
    width: auto;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  @media (min-width: 768px) {
    .message.user {
      max-width: 75%;
      padding: 20px;
    }
  }

  .quick-answers {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 0;
    width: 100%;
  }

  .quick-answers button {
    border-radius: 20px;
    padding: 8px 14px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;
    flex: 1 1 auto;
    min-width: 0;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (min-width: 640px) {
    .quick-answers button {
      flex: 0 1 auto;
      padding: 10px 20px;
      font-size: 14px;
    }
  }

  .notice.error {
    border-radius: 12px;
    padding: 14px;
    margin: 12px 0;
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  @media (min-width: 768px) {
    .notice.error {
      padding: 16px;
      margin: 16px 0;
    }
  }

  .notice.info {
    border-radius: 12px;
    padding: 14px;
    margin: 12px 0;
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  @media (min-width: 768px) {
    .notice.info {
      padding: 16px;
      margin: 16px 0;
    }
  }

  .eyebrow {
    border-radius: 20px;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    display: inline-block;
  }

  .section-index {
    font-size: 14px;
    font-weight: 700;
  }

  .loading-screen {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 24px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .orb {
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.05); }
  }

  .assistant-avatar {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .revision-finish {
    border-radius: 10px;
    padding: 10px 16px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .products-toggle {
    border-radius: 10px;
    padding: 10px 16px;
    font-size: 14px;
    cursor: pointer;
  }

  .products-toggle span {
    border-radius: 10px;
    padding: 2px 8px;
    margin-right: 8px;
    font-size: 12px;
  }

  .summary-tags span {
    border-radius: 8px;
    padding: 6px 12px;
    font-size: 13px;
  }

  blockquote {
    padding: 16px;
    margin: 16px 0;
    border-radius: 0 12px 12px 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  .feature-list {
    list-style: none;
    padding: 0;
    margin: 20px 0;
  }

  .feature-list li {
    padding: 8px 0;
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 15px;
  }

  .feature-list svg {
    flex-shrink: 0;
  }

  .typing i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    animation: bounce 1.4s ease-in-out infinite both;
  }

  .typing i:nth-child(1) { animation-delay: -0.32s; }
  .typing i:nth-child(2) { animation-delay: -0.16s; }

  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
    40% { transform: scale(1); opacity: 1; }
  }

  .conversation-retention-note {
    font-size: 13px;
    text-align: center;
    padding: 12px;
  }

  .designer-toolbar-link {
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 10px;
    font-size: 14px;
    transition: all 0.2s ease;
  }

  .chat-layout {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  @media (min-width: 1024px) {
    .chat-layout {
      flex-direction: row;
      gap: 24px;
    }
  }

  .room-aside {
    order: -1;
    width: 100%;
  }

  @media (min-width: 1024px) {
    .room-aside {
      order: 0;
      width: 320px;
      flex-shrink: 0;
    }
  }

  .chat-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .preview-grid {
    display: grid;
    gap: 16px;
    width: 100%;
  }

  @media (min-width: 768px) {
    .preview-grid {
      grid-template-columns: 1fr 1fr;
      gap: 24px;
    }
  }

  .product-grid {
    display: grid;
    gap: 16px;
    grid-template-columns: 1fr;
    width: 100%;
  }

  @media (min-width: 640px) {
    .product-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (min-width: 1024px) {
    .product-grid {
      grid-template-columns: repeat(3, 1fr);
      gap: 20px;
    }
  }

  .stage-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 24px;
    width: 100%;
  }

  @media (min-width: 640px) {
    .stage-actions {
      flex-direction: row;
      justify-content: space-between;
    }
  }

  .auth-stage {
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
  }

  @media (min-width: 1024px) {
    .auth-stage {
      flex-direction: row;
      align-items: flex-start;
    }
  }

  .image-column {
    flex: 1;
    min-width: 0;
  }

  .form-card {
    flex: 1;
    padding: 20px;
    min-width: 0;
  }

  @media (min-width: 768px) {
    .form-card {
      padding: 32px;
    }
  }

  .upload-stage {
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
  }

  @media (min-width: 1024px) {
    .upload-stage {
      flex-direction: row;
      align-items: flex-start;
    }
  }

  .stage-intro {
    flex: 1;
    min-width: 0;
  }

  .upload-panel {
    flex: 1;
    padding: 20px;
    min-width: 0;
  }

  @media (min-width: 768px) {
    .upload-panel {
      padding: 32px;
    }
  }

  h1 {
    font-size: 22px;
    line-height: 1.3;
    margin: 12px 0;
    word-wrap: break-word;
  }

  @media (min-width: 768px) {
    h1 {
      font-size: 32px;
    }
  }

  @media (min-width: 1024px) {
    h1 {
      font-size: 40px;
    }
  }

  h2 {
    font-size: 18px;
    line-height: 1.4;
    margin: 12px 0;
    word-wrap: break-word;
  }

  @media (min-width: 768px) {
    h2 {
      font-size: 24px;
    }
  }

  h3 {
    font-size: 16px;
    margin: 12px 0;
    word-wrap: break-word;
  }

  p {
    line-height: 1.7;
    font-size: 15px;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  @media (min-width: 768px) {
    p {
      font-size: 16px;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  table {
    width: 100%;
    max-width: 100%;
    border-collapse: collapse;
  }

  td, th {
    padding: 12px;
    text-align: right;
  }

  @media (max-width: 767px) {
    table, thead, tbody, th, td, tr {
      display: block;
    }
    
    thead tr {
      position: absolute;
      top: -9999px;
      left: -9999px;
    }
    
    td {
      border: none;
      position: relative;
      padding-right: 50%;
    }
    
    td:before {
      content: attr(data-label);
      position: absolute;
      right: 12px;
      font-weight: 600;
    }
  }
`}</style>





    <header className="designer-toolbar">
      <div className="designer-toolbar-copy">
        <Link to="/" className="designer-toolbar-link designer-home-link" aria-label="صفحه اصلی" title="صفحه اصلی"><Home /><span>صفحه اصلی</span></Link>
      </div>
      <button className="designer-toolbar-link danger" aria-label="شروع طراحی جدید" title="شروع طراحی جدید" disabled={store.busy} onClick={() => void startNewDesign()}><RotateCcw /> <span>شروع طراحی جدید</span></button>
    </header>
    <FlowStepper active={activeStep} />
    {store.error && <div className={`notice ${store.error.includes('کد آزمایشی') || store.error.includes('جایگزین محلی') ? 'info' : 'error'}`}><AlertCircle size={18} /><span>{store.error}</span><button onClick={() => store.setError(null)}>×</button></div>}
    {limitReached && state.status === 'ACTIVE' && <div className="daily-limit-banner" role="status"><AlertCircle /><div><strong>سقف استفاده امروز تکمیل شده است</strong><span>{limitMessage}</span></div></div>}
    {!state.environmentImageUrl && <UploadStage file={file} preview={filePreview} busy={store.busy} onFile={handleFile} onSubmit={uploadRoom} />}
    {state.environmentImageUrl && state.status !== 'ACTIVE' && <OtpStage name={name} phone={phone} code={code} sent={otpSent} busy={store.busy} expiresIn={otpExpiresIn} resendIn={otpResendIn} sendCount={otpSendCount} feedback={otpFeedback} verified={otpVerified} inputRef={otpInputRef} onName={setName} onPhone={setPhone} onCode={setCode} onSend={sendOtp} onVerify={verify} onEditIdentity={editOtpIdentity} />}
    {state.status === 'ACTIVE' && revisionMode && <ChatStage state={{ ...state, messages, recommendations }} input={input} busy={store.busy} limitReached={limitReached} limitMessage={limitMessage} showProducts={showProducts} revisionMode onInput={setInput} onSend={sendMessage} onFinishRevision={finishRevision} onToggleRecommendation={toggleRecommendation} onToggleProducts={() => setShowProducts((value) => !value)} messagesViewport={messagesViewport} />}
    {state.status === 'ACTIVE' && !revisionMode && !revisionPreview && !completeProfile(state.requirements) && !generation && <ChatStage state={{ ...state, messages, recommendations }} input={input} busy={store.busy} limitReached={limitReached} limitMessage={limitMessage} showProducts={showProducts} revisionMode={false} onInput={setInput} onSend={sendMessage} onFinishRevision={finishRevision} onToggleRecommendation={toggleRecommendation} onToggleProducts={() => setShowProducts((value) => !value)} messagesViewport={messagesViewport} />}
    {state.status === 'ACTIVE' && !revisionPreview && completeProfile(state.requirements) && !revisionMode && !generation && <PreviewStage state={{ ...state, recommendations }} busy={store.busy} onBack={() => void enterRevisionConversation(false)} onToggleRecommendation={toggleRecommendation} onRemove={removeProduct} onConfirm={confirmAndGenerate} />}
    {state.status === 'ACTIVE' && revisionPreview && generation && <PreviewStage state={{ ...state, recommendations }} busy={store.busy} revision={{ previous: generation, onCancel: () => { setRevisionPreview(false); setRevisionFromResult(false); } }} onBack={() => void enterRevisionConversation(true)} onToggleRecommendation={toggleRecommendation} onRemove={removeProduct} onConfirm={confirmAndGenerate} />}
    {!revisionMode && !revisionPreview && generation && ['PENDING', 'PROCESSING'].includes(generation.status) && <GenerationStage image={state.environmentImageUrl!} generation={generation} />}
    {!revisionMode && !revisionPreview && generation?.status === 'FAILED' && <FailureStage message={generation.errorMessage || 'تولید تصویر ناموفق بود.'} onRetry={() => setGeneration(null)} />}
    {!revisionMode && !revisionPreview && generation?.status === 'COMPLETED' && generation.resultImageUrl && <ResultStage generation={generation} history={generations.filter((item) => item.status === 'COMPLETED' && item.resultImageUrl)} selectedProductIds={selectedCartProductIds} busy={store.busy} limitReached={limitReached} onToggleProduct={(productId: string) => setSelectedCartProductIds((current) => current.includes(productId) ? current.filter((id) => id !== productId) : [...current, productId])} onSelectHistory={setGeneration} onModify={() => void beginAction('modify')} onCart={() => void beginAction('cart', selectedCartProductIds)} />}
    <SurveyModal open={surveyOpen} busy={store.busy} onClose={() => { setSurveyOpen(false); setPendingAction(null); setPendingCartProductIds([]); closeCartTab(); }} onSubmit={submitSurvey} />
  </main>;
}

function LoadingScreen({ label }: { label: string }) { return <div className="loading-screen"><span className="orb"><Sparkles /></span><LoaderCircle className="spin" /><p>{label}</p></div>; }
function InitializationFailure({ message, onRetry }: { message: string | null; onRetry: () => void }) { return <section className="failure-card initialization-failure"><AlertCircle /><h2>ارتباط با سرور هنوز برقرار نشده است</h2><p>{message || 'ممکن است API هنوز در حال راه‌اندازی باشد. چند لحظه بعد دوباره تلاش کنید.'}</p><button className="primary-button" onClick={onRetry}><RefreshCw />تلاش مجدد بدون Refresh صفحه</button><Link to="/" className="text-link">بازگشت به صفحه اصلی</Link></section>; }
function messageOf(error: unknown) { return error instanceof ApiClientError || error instanceof Error ? error.message : 'خطای نامشخصی رخ داد.'; }

function UploadStage({ file, preview, busy, onFile, onSubmit }: { file: File | null; preview: string | null; busy: boolean; onFile: (file?: File) => void; onSubmit: () => void }) {
  return <section className="stage upload-stage" aria-labelledby="upload-title">
    <div className="upload-copy-column">
      <div className="stage-intro">
        <span className="eyebrow">شروع طراحی</span>
        <h1 id="upload-title">گام اول، یک عکس روشن از فضای خودتان.</h1>
      </div>
      <div className="upload-supporting-copy">
        <p>مشاور هوشمند کارو لایت بعد از دیدن فضا، چند سؤال کوتاه می‌پرسد و محصولات واقعی فروشگاه را در همان تصویر قرار می‌دهد.</p>
        <div className="design-benefits" aria-label="مزایای طراحی">
          <span><CheckCircle2 /> چیدمان اتاق حفظ می‌شود</span>
          <span><CheckCircle2 /> پیشنهادها از کاتالوگ واقعی‌اند</span>
          <span><ShieldCheck /> تصویر فقط برای ساخت طراحی استفاده می‌شود</span>
        </div>
      </div>
    </div>
    <div className="upload-panel">
      <label className={`dropzone ${preview ? 'has-file confirmed-preview' : ''}`} htmlFor="room-image-input" onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); onFile(event.dataTransfer.files[0]); }}>
        <input id="room-image-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => { onFile(event.target.files?.[0]); event.currentTarget.value = ''; }} />
        {preview ? <><img src={preview} alt="پیش‌نمایش اتاق" /><span className="preview-ready"><CheckCircle2 /> عکس آماده است</span></> : <><span className="dropzone-art" aria-hidden="true"><i /><i /><i /><b /></span><span className="upload-icon"><UploadCloud size={30} /></span><strong>عکس فضای موردنظر را انتخاب کنید</strong><p>نمای کامل اتاق و نور مناسب، نتیجه دقیق‌تری می‌دهد.</p><small>JPG، PNG یا WEBP · حداکثر ۱۰ مگابایت</small></>}
      </label>
      {preview && <div className="upload-confirm-actions"><label className="secondary-button" htmlFor="room-image-input"><RefreshCw />انتخاب عکس دیگر</label><button className="primary-button" disabled={!file || busy} onClick={onSubmit}>{busy ? <><LoaderCircle className="spin" /> در حال بارگذاری…</> : <><ImagePlus />استفاده از این عکس</>}</button></div>}
    </div>
  </section>;
}

const formatOtpTime = (seconds: number) => `${Math.floor(seconds / 60).toLocaleString('fa-IR')}:${(seconds % 60).toLocaleString('fa-IR', { minimumIntegerDigits: 2 })}`;

function OtpStage(props: { name: string; phone: string; code: string; sent: boolean; busy: boolean; expiresIn: number; resendIn: number; sendCount: number; feedback: OtpFeedback; verified: boolean; inputRef: { current: HTMLInputElement | null }; onName: (v: string) => void; onPhone: (v: string) => void; onCode: (v: string) => void; onSend: () => void; onVerify: () => void; onEditIdentity: () => void }) {
  const canResend = props.sent && props.expiresIn === 0 && props.sendCount < MAX_OTP_SENDS && !props.busy;

  return <section className="stage auth-stage auth-stage-compact" aria-labelledby="otp-title">
    <div className="form-card">
      <span className="eyebrow"><ShieldCheck size={14} /> ورود امن</span>
      <h2 id="otp-title">{props.sent ? 'کد چهاررقمی را وارد کنید' : 'مدیریت طراحی ها'}</h2>
      {!props.sent && <p>با تأیید شماره موبایل، طراحی و گفت‌وگوی شما برای مراجعات بعدی محفوظ می‌ماند.</p>}

      {!props.sent ? <>
        <label className="field"><span>نام و نام خانوادگی</span><input autoComplete="name" value={props.name} onChange={(e) => props.onName(e.target.value)} placeholder="مثال: سارا محمدی" /></label>
        <label className="field"><span>شماره موبایل</span><input dir="ltr" inputMode="tel" autoComplete="tel" value={props.phone} onChange={(e) => props.onPhone(normalizePhoneInput(e.target.value))} placeholder="09123456789" /></label>
        <button className="primary-button wide" disabled={props.busy || props.name.trim().length < 2 || !/^09\d{9}$/.test(props.phone)} onClick={props.onSend}>{props.busy ? <><LoaderCircle className="spin" />در حال ارسال…</> : 'ارسال کد تأیید'}</button>
      </> : <>
        <div className="otp-identity"><span><strong>{props.name}</strong><small dir="ltr">{props.phone}</small></span><button type="button" onClick={props.onEditIdentity} disabled={props.busy || props.verified}><Pencil size={15} /> ویرایش</button></div>
        {/* <fieldset className="otp-fieldset"><legend>کد تأیید چهاررقمی</legend><div className="otp-boxes" dir="ltr">{digits.map((digit, index) => <input key={index} ref={(element) => { digitRefs.current[index] = element; if (index === 0) props.inputRef.current = element; }} value={digit} inputMode="numeric" autoComplete={index === 0 ? 'one-time-code' : 'off'} maxLength={1} aria-label={`رقم ${index + 1} کد تأیید`} aria-invalid={props.feedback?.tone === 'error'} disabled={props.verified} onChange={(event) => setDigit(index, event.target.value)} onKeyDown={(event) => { if (event.key === 'Backspace' && !digit && index > 0) { event.preventDefault(); const next = [...digits]; next[index - 1] = ''; props.onCode(next.join('')); digitRefs.current[index - 1]?.focus(); } else if (event.key === 'ArrowLeft' && index < 3) digitRefs.current[index + 1]?.focus(); else if (event.key === 'ArrowRight' && index > 0) digitRefs.current[index - 1]?.focus(); else if (event.key === 'Enter' && props.code.length === 4) props.onVerify(); }} onPaste={(event) => { const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4); if (pasted) { event.preventDefault(); props.onCode(pasted); digitRefs.current[Math.min(pasted.length, 3)]?.focus(); } }} />)}</div></fieldset> */}
        <fieldset className="otp-fieldset">
          <legend>کد تأیید چهاررقمی</legend>

          <div
            className="otp-boxes"
            dir="ltr"
            onClick={() => props.inputRef.current?.focus()}
          >
            <input
              ref={(element) => {
                props.inputRef.current = element;
              }}
              className="otp-native-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={4}
              pattern="[0-9]*"
              value={props.code}
              disabled={props.verified}
              aria-label="کد تأیید چهاررقمی"
              aria-invalid={props.feedback?.tone === 'error'}
              onChange={(event) => {
                const value = event.target.value
                  .replace(/\D/g, '')
                  .slice(0, 4);

                props.onCode(value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && props.code.length === 4) {
                  props.onVerify();
                }
              }}
            />

            {Array.from({ length: 4 }, (_, index) => (
              <span
                key={index}
                className={`otp-box ${props.code[index] ? 'filled' : ''} ${index === props.code.length ? 'active' : ''
                  }`}
                aria-hidden="true"
              >
                {props.code[index] ?? ''}
              </span>
            ))}
          </div>
        </fieldset>
        {props.feedback && <div className={`otp-feedback ${props.feedback.tone}`} role={props.feedback.tone === 'error' ? 'alert' : 'status'}>{props.feedback.tone === 'success' ? <CheckCircle2 /> : props.feedback.tone === 'error' ? <AlertCircle /> : <ShieldCheck />}<span>{props.feedback.message}</span></div>}
        <div className={`otp-timer-line ${props.expiresIn <= 0 ? 'expired' : ''}`} role="status"><Timer />{props.expiresIn > 0 ? <span>زمان باقی‌مانده <b>{formatOtpTime(props.expiresIn)}</b></span> : <><span>زمان استفاده از کد تمام شد، یک کد تازه دریافت کنید.</span>{canResend && <button type="button" onClick={props.onSend}>دریافت کد تازه</button>}</>}</div>
        <button className="primary-button wide" disabled={props.busy || props.verified || props.expiresIn <= 0 || props.code.length !== 4} onClick={props.onVerify}>{props.verified ? <><CheckCircle2 />تأیید شد</> : props.busy ? <><LoaderCircle className="spin" />در حال بررسی…</> : 'تأیید و شروع گفتگو'}</button>
      </>}
      {!props.sent && props.feedback && <div className={`otp-feedback ${props.feedback.tone}`} role="alert"><AlertCircle /><span>{props.feedback.message}</span></div>}
      <small className="privacy-note"><ShieldCheck size={14} /> اطلاعات تماس شما فقط برای ورود، نگهداری طراحی و کنترل سقف استفاده به‌کار می‌رود.</small>
    </div>
  </section>;
}

function ChatStage({ state, input, busy, limitReached, limitMessage, showProducts, revisionMode, onInput, onSend, onFinishRevision, onToggleRecommendation, onToggleProducts, messagesViewport }: any) {
  const messages = Array.isArray(state.messages) ? state.messages : [];
  const recommendations = Array.isArray(state.recommendations) ? state.recommendations : [];
  const selectedCount = recommendations.filter((item: Product) => item.selected).length;
  return <section className="chat-layout" aria-label={revisionMode ? 'گفت‌وگوی اصلاح طراحی' : 'گفت‌وگو با مشاور طراحی'}>
    <aside className="room-aside">
      <details className="room-preview">
        <summary><img src={state.environmentImageUrl} alt="" /><span><strong>تصویر مرجع</strong><small>عکسی که طراحی روی آن انجام می‌شود</small></span></summary>
        <div className="sticky-image"><img src={state.environmentImageUrl} alt="تصویر مرجع اتاق" /><span>تصویر مرجع طراحی</span></div>
      </details>
      <RequirementPanel requirements={state.requirements as Requirements} />
      {recommendations.length > 0 && <button className="products-toggle aside-products-toggle" aria-expanded={showProducts} onClick={onToggleProducts}><PackageSearch size={18} /><span>محصولات پیشنهادی</span><b>{selectedCount.toLocaleString('fa-IR')} انتخاب</b><ChevronDown /></button>}
    </aside>

    <div className="chat-card">
      <header><span className="assistant-avatar"><Bot /></span><div><h2>{revisionMode ? 'اصلاح طراحی با مشاور' : 'مشاور طراحی کارو لایت'}</h2><p><i /> پاسخ‌گو و آماده راهنمایی</p></div>{revisionMode && <button className="revision-finish" disabled={busy || limitReached} onClick={onFinishRevision}><CheckCircle2 /> بررسی تغییرات</button>}</header>
      <div className="messages" ref={messagesViewport} aria-live="polite">{messages.map((message: any) => <div key={message.id} className={`message ${message.role}`}><p>{message.content}</p><time>{new Date(message.createdAt).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' })}</time></div>)}{busy && <div className="message assistant typing" aria-label="مشاور در حال نوشتن است"><i /><i /><i /></div>}</div>
      <footer>
        {limitReached ? <div className="chat-limit-lock"><AlertCircle /><span>{limitMessage}</span></div> : <><div className="chat-input"><textarea rows={1} maxLength={3000} value={input} onChange={(e) => onInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }} placeholder={revisionMode ? 'مثلاً نور را گرم‌تر کن یا محصول دیگری پیشنهاد بده…' : 'درخواست خود را بنویسید...'} aria-label="پاسخ به مشاور" /><button disabled={!input.trim() || busy} onClick={() => onSend()} aria-label="ارسال پیام"><Send /></button></div><small className="free-answer-note">Enter برای ارسال · Shift + Enter برای خط جدید</small></>}
      </footer>
    </div>

    {showProducts && <><button className="product-sheet-backdrop" aria-label="بستن محصولات پیشنهادی" onClick={onToggleProducts} /><aside className="identified-products" aria-label="محصولات پیشنهادی"><header><div><h3>محصولات پیشنهادی</h3><p>حداکثر سه محصول را برای استفاده در تصویر انتخاب کنید.</p></div><button type="button" onClick={onToggleProducts} aria-label="بستن"><X /></button></header>{recommendations.length ? <div className="recommendation-selector compact-list">{recommendations.map((product: Product) => <RecommendationOption key={product.id} product={product} busy={busy} disabled={selectedCount >= 3 && !product.selected} onToggle={() => onToggleRecommendation(product)} compact />)}</div> : <p className="empty-products">پس از تکمیل گفتگو نمایش داده می‌شوند.</p>}</aside></>}
  </section>;
}

function PreviewStage({ state, busy, revision, onBack, onToggleRecommendation, onConfirm }: any) {
  const r = state.requirements as Requirements;
  const recommendations = (state.recommendations ?? []) as Product[];
  const selectedProducts = (state.recommendations ?? []).filter((product: Product) => product.selected);
  const items = [['فضا', r.roomType], ['سبک', r.style], ['حال‌وهوا', r.atmosphere], ['بودجه', budgetLabels[r.budgetTier || ''] || r.budgetTier], ['کاربرد', r.useCase]];
  return <section className="preview-stage preview-stage-streamlined">
    <header className="stage-heading"><span className="eyebrow">{revision ? 'پیش‌نمایش اصلاحات' : 'پیش‌نمایش طراحی'}</span><h1>{revision ? 'نسخه تازه آماده تأیید است.' : 'نیازها را تأیید کنید و تصویر را بسازید.'}</h1></header>
    <div className="preview-primary-grid">
      <div className="direction-image preview-room-image"><img src={state.environmentImageUrl} alt="تصویر مرجع اتاق" /><span>تصویر مرجع</span></div>
      <div className="summary-card preview-summary"><h2>خلاصه نیازهای شما</h2><dl>{items.slice(0, 3).map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || '—'}</dd></div>)}</dl>
        <details className="preview-secondary"><summary>اطلاعات تکمیلی <ChevronDown /></summary><div><dl>{items.slice(3).map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value || '—'}</dd></div>)}</dl>{r.preferredCategories?.length > 0 && <div className="summary-tags">{r.preferredCategories.map((x) => <span key={x}>{x}</span>)}</div>}{r.features?.length > 0 && <div className="summary-tags">{r.features.map((x) => <span key={x}>{x}</span>)}</div>}{r.visualDirection && <blockquote>{r.visualDirection}</blockquote>}<p>{r.summary || 'محصولات منتخب با حفظ فضای فعلی به تصویر اضافه می‌شوند.'}</p></div></details>
      </div>
    </div>
    {revision && <details className="revision-context"><summary><History />نسخه فعلی محفوظ می‌ماند<ChevronDown /></summary><div><img src={revision.previous.resultImageUrl} alt="نسخه فعلی طراحی" /><p>خروجی جدید به تاریخچه اضافه می‌شود و نسخه قبلی حذف نخواهد شد.</p></div></details>}
    <details className="recommendation-block preview-products"><summary><span><PackageSearch /><b>محصولات پیشنهادی</b><small>{selectedProducts.length.toLocaleString('fa-IR')} محصول انتخاب‌شده</small></span><ChevronDown /></summary><div className="preview-products-content"><p className="block-help">حداکثر سه محصول را برای استفاده در تصویر انتخاب کنید.</p><div className="recommendation-selector">{recommendations.map((product: Product) => <RecommendationOption key={product.id} product={product} busy={busy} disabled={selectedProducts.length >= 3 && !product.selected} onToggle={() => onToggleRecommendation(product)} />)}</div></div></details>
    <div className="stage-actions preview-actions"><button className="secondary-button" onClick={onBack}><ArrowLeft />اصلاح نیازها</button>{revision && <button className="text-button" onClick={revision.onCancel}>مشاهده نتیجه قبلی</button>}<button className="primary-button" disabled={busy || selectedProducts.length === 0} onClick={onConfirm}>{busy ? <><LoaderCircle className="spin" />در حال شروع…</> : <><WandSparkles />{revision ? 'تولید نسخه جدید' : 'تأیید و ساخت تصویر'}</>}</button></div>
  </section>;
}

function RecommendationOption({ product, busy, onToggle, compact = false, disabled = false }: { product: Product; busy: boolean; onToggle: () => void; compact?: boolean; disabled?: boolean }) {
  return <article className={`recommendation-option ${product.selected ? 'selected' : ''} ${compact ? 'compact' : ''} ${disabled ? 'limit-reached' : ''}`} style={disabled ? { opacity: 0.45, pointerEvents: 'none' } : undefined}><label><input type="checkbox" checked={Boolean(product.selected)} disabled={busy || disabled} onChange={onToggle} /><span className="recommendation-check"><CheckCircle2 /></span><b>{product.selected ? 'در طراحی استفاده می‌شود' : disabled ? 'ظرفیت انتخاب تکمیل شده' : 'انتخاب این محصول'}</b></label><ProductCard product={product} compact={compact} /></article>;
}

function GenerationStage({ image, generation }: { image: string; generation: Generation }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, [generation.id]);
  // سرور تا پایان تحلیل بینایی، پرامپت را با متن جای‌نگهدار ذخیره می‌کند؛ به‌روزشدن این
  // فیلد یعنی تحلیل و انتخاب محصولات تمام شده و تولید تصویر نهایی آغاز شده است.
  const analysisDone = Boolean(generation.prompt && !generation.prompt.startsWith('در حال آماده‌سازی'));
  const steps = [
    { title: 'دریافت و آماده‌سازی تصویر شما', state: elapsed >= 2 ? 'done' : 'active' },
    { title: 'تحلیل تصویر و انتخاب محصولات کاتالوگ', state: analysisDone ? 'done' : elapsed >= 2 ? 'active' : 'idle' },
    { title: 'نوشتن دستور دقیق طراحی', state: analysisDone ? 'done' : 'idle' },
    { title: 'جاسازی محصولات واقعی در تصویر شما', state: analysisDone ? 'active' : 'idle' },
  ] as const;
  const percent = analysisDone ? Math.min(60 + Math.round(elapsed / 6), 96) : Math.min(5 + Math.round(elapsed / 2), 55);
  const clock = `${Math.floor(elapsed / 60).toLocaleString('fa-IR')}:${(elapsed % 60).toLocaleString('fa-IR', { minimumIntegerDigits: 2 })}`;
  return <section className="generation-stage"><div className="generation-visual"><img src={image} alt="اتاق در حال طراحی" /><span className="scan-line" /><div className="generation-orb"><WandSparkles /></div></div><div className="generation-copy"><span className="eyebrow">پردازش تصویر با هوش مصنوعی</span><h1>محصول مناسب پیدا شد؛ حالا نور را به اتاق می‌آوریم.</h1><p>هوش مصنوعی در حال تحلیل مقیاس، زاویه، سایه و بازتاب محصول در فضای واقعی شماست.</p>
    <ol className="generation-steps">{steps.map((step) => <li key={step.title} className={step.state}>{step.state === 'done' ? <CheckCircle2 size={17} /> : step.state === 'active' ? <LoaderCircle className="spin" size={17} /> : <span className="step-dot" />}<span>{step.title}</span>{step.state === 'active' && <i className="step-pulse" />}</li>)}</ol>
    <div className="generation-meter"><div className="progress-track filled"><i style={{ width: `${percent}%` }} /></div><span className="generation-percent">{percent.toLocaleString('fa-IR')}٪</span><span className="generation-clock"><Timer size={15} /> {clock}</span></div>
    <small><LoaderCircle className="spin" /> این پنجره را باز نگه دارید؛ در پایان به‌صورت خودکار به نتیجه می‌روید.</small></div></section>;
}
function FailureStage({ message, onRetry }: { message: string; onRetry: () => void }) { return <section className="failure-card"><AlertCircle /><h2>تولید تصویر کامل نشد</h2><p>{message}</p><button className="primary-button" onClick={onRetry}><RefreshCw />بازگشت و تلاش دوباره</button></section>; }

async function downloadDesignImage(url: string) {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('download failed');
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = 'karolight-design.png';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
}

function ComparisonLightbox({ before, after, onClose }: { before: string; after: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = previousOverflow; };
  }, [onClose]);
  return <div className="comparison-lightbox" role="dialog" aria-modal="true" aria-label="مقایسه تمام‌صفحه طراحی" onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="lightbox-bar"><strong>مقایسه قبل و بعد طراحی</strong><div className="lightbox-actions"><button type="button" className="primary-button" onClick={() => void downloadDesignImage(after)}><Download size={16} />دانلود تصویر نهایی</button><button type="button" className="icon-button" onClick={onClose} aria-label="بستن"><X size={18} /></button></div></div>
    <div className="lightbox-comparison"><BeforeAfter before={before} after={after} /></div>
    <small className="lightbox-hint">خط مقایسه را بکشید؛ با کلیدهای ← → هم جابه‌جا می‌شود. Esc می‌بندد.</small>
  </div>;
}

function ResultStage({ generation, history, selectedProductIds, busy, limitReached, onToggleProduct, onSelectHistory, onModify, onCart }: any) {
  const [fullscreen, setFullscreen] = useState(false);
  const selectedCount = selectedProductIds.length;
  const selectedProducts = (generation.products as Product[]).filter((product: Product) => selectedProductIds.includes(product.id));
  const pricedTotal = selectedProducts.reduce((total: number, product: Product) => total + (product.price ?? 0), 0);
  const unpricedCount = selectedProducts.filter((product: Product) => !product.price).length;
  return <section className="result-stage result-stage-streamlined"><header className="stage-heading"><span className="eyebrow">طراحی نهایی</span><h1>تصویر شما آماده است.</h1></header>
    <div className="comparison-wrap result-reveal"><BeforeAfter before={generation.originalImageUrl} after={generation.resultImageUrl} /><div className="comparison-tools"><button type="button" className="icon-button" onClick={() => setFullscreen(true)} aria-label="نمایش تمام‌صفحه" title="نمایش تمام‌صفحه"><Maximize2 size={17} /></button><button type="button" className="icon-button" onClick={() => void downloadDesignImage(generation.resultImageUrl)} aria-label="دانلود تصویر نهایی" title="دانلود تصویر نهایی"><Download size={17} /></button></div></div>
    {fullscreen && <ComparisonLightbox before={generation.originalImageUrl} after={generation.resultImageUrl} onClose={() => setFullscreen(false)} />}
    <div className="purchase-bar" role="region" aria-label="خرید محصولات انتخاب‌شده">
      <div className="purchase-total"><span>جمع {selectedCount.toLocaleString('fa-IR')} محصول انتخاب‌شده</span>{selectedCount > 0 && <strong>{pricedTotal > 0 ? `${pricedTotal.toLocaleString('fa-IR')} تومان` : 'استعلام قیمت'}</strong>}{unpricedCount > 0 && <small>+ {unpricedCount.toLocaleString('fa-IR')} قلم استعلام قیمت از فروشگاه</small>}</div>
      <div className="purchase-actions"><button className="secondary-button" disabled={busy || limitReached} onClick={onModify}><RefreshCw />{limitReached ? 'سقف اصلاح امروز تکمیل است' : 'اصلاح طراحی'}</button><button className="primary-button" disabled={busy || selectedCount === 0} onClick={onCart}>{busy ? <><LoaderCircle className="spin" />در حال انتقال به سبد خرید…</> : <><ShoppingCart />افزودن به سبد خرید</>}</button></div>
    </div>
    <details className="result-details result-products-details" open><summary><span><ShoppingCart /><b>محصولات به‌کاررفته</b><small>{generation.products.length.toLocaleString('fa-IR')} محصول</small></span><ChevronDown /></summary><div className="cart-product-picker"><p>محصولات موردنظر برای سبد خرید را انتخاب کنید.</p><div className="result-products">{generation.products.map((product: Product) => <article key={product.id} className={`cart-product-option ${selectedProductIds.includes(product.id) ? 'selected' : ''}`}><label className="cart-selector"><input type="checkbox" checked={selectedProductIds.includes(product.id)} onChange={() => onToggleProduct(product.id)} /><span className="cart-check"><CheckCircle2 /></span><b>{selectedProductIds.includes(product.id) ? 'انتخاب‌شده' : 'انتخاب محصول'}</b></label><ProductCard product={product} compact /></article>)}</div></div></details>
    {history.length > 1 && <details className="generation-history result-secondary-details"><summary><span><History /><b>نسخه‌های قبلی طراحی</b><small>{history.length.toLocaleString('fa-IR')} نسخه</small></span><ChevronDown /></summary><div className="history-items">{history.map((item: Generation, index: number) => <button key={item.id} className={item.id === generation.id ? 'active' : ''} onClick={() => onSelectHistory(item)}><img src={item.resultImageUrl!} alt={`نسخه ${history.length - index}`} /><span>نسخه {history.length - index}</span></button>)}</div></details>}
  </section>;
}
